import {ButtonCancel, ScheduleTimeSlotItem, TaskCard, Tooltip} from '..'
import {getTaskContentName, TempTask} from '../../model'
import {ITask} from '../../shared/db'
import {selectTheme} from '../../store'
import {Calendar as BigCalendar, momentLocalizer, Event, SlotInfo} from 'react-big-calendar'
import DatePicker from 'react-datepicker'
import styled from '@emotion/styled'
import {CSSProperties, useState} from 'react'
import {ChevronDownIcon} from '../../lib/svg/icon/chevron_down'
import {format} from 'date-fns'
import _ from 'lodash'
import moment from 'moment'
import {useDebounce} from 'react-use'
const localizer = momentLocalizer(moment)
interface FullCalendarViewProps<T extends ITask | TempTask> {
  scheduleTimeSlotList: ScheduleTimeSlotItem[]
  taskList: T[]
  setShowFullCalendar: (show: boolean) => void
  startDate: Date
  setStartDate: (date: Date) => void
  simulationDuration: number | string
  setSimulationDuration: (duration: number | string) => void
  isEditingLiveProject: boolean
}

export const FullCalendarView = <T extends ITask | TempTask>(props: FullCalendarViewProps<T>) => {
  const {
    scheduleTimeSlotList,
    taskList,
    setShowFullCalendar,
    startDate,
    setStartDate,
    simulationDuration,
    setSimulationDuration,
    isEditingLiveProject,
  } = props
  const {color, fontSize, fontWeight} = selectTheme()
  const [bigCalendarDate, setBigCalendarDate] = useState(startDate)
  const [duration, setDuration] = useState<number | string>(simulationDuration)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const DatePickerContainer = styled.div`
    input {
      width: 160px;
      height: 32px;
      padding: 6px 12px;
      border-radius: 5px;
      font-size: ${fontSize.h7};
      color: ${color.textIcon.secondary};
      border: 1px solid ${color.border.default};
    }

    .react-datepicker-popper {
      z-index: 1001;
      position: absolute !important;
      margin-left: 20px;
    }
  `
  const taskColorTranslator = (taskColor: string) => {
    switch (`#${taskColor}`) {
      case color.task.green:
        return {backgroundColor: color.tag.green.light, borderLeft: `2px solid ${color.tag.green.dark}`}
      case color.task.blue:
        return {backgroundColor: color.tag.blue.light, borderLeft: `2px solid ${color.tag.blue.dark}`}
      case color.task.purple:
        return {backgroundColor: color.tag.purple.light, borderLeft: `2px solid ${color.tag.purple.dark}`}
      case color.task.red:
      case '#EC5151':
        return {backgroundColor: color.tag.red.light, borderLeft: `2px solid ${color.tag.red.dark}`}
      case color.task.yellow:
        return {backgroundColor: color.tag.yellow.light, borderLeft: `2px solid ${color.tag.yellow.dark}`}
      case color.task.orange:
        return {backgroundColor: color.tag.orange.light, borderLeft: `2px solid ${color.tag.orange.dark}`}
      case color.task.grey:
      case '#9C9EAE':
        return {backgroundColor: color.tag.grey.light, borderLeft: `2px solid ${color.tag.grey.dark}`}
      default:
        return {backgroundColor: color.tag.green.light, borderLeft: `2px solid ${color.tag.green.dark}`}
    }
  }

  const scheduleTaskList = taskList.filter((task) => {
    const hasSchedule = task.scheduleList?.length
    if (isEditingLiveProject) {
      const hasLiveEditingScheduleQueue = (task as TempTask).liveEditingScheduleQueue?.length
      return hasSchedule || hasLiveEditingScheduleQueue
    } else {
      return hasSchedule
    }
  })

  const handleDayClick = (slotInfo: SlotInfo) => {
    setSelectedDate(slotInfo.start)
  }

  const handleBackToAllTasks = () => {
    setSelectedDate(null)
  }

  // Get tasks for the selected day
  const getTasksForSelectedDay = () => {
    if (!selectedDate) return []

    const selectedMoment = moment(selectedDate).startOf('day')

    // Find all schedule slots that occur on the selected day
    const taskIdsForDay = new Set<string>()
    scheduleTimeSlotList.forEach(({scheduleId, startDate: slotStartDate, endDate}) => {
      const slotStart = moment(new Date(slotStartDate.replace('Z', ''))).startOf('day')
      let slotEnd
      if (!endDate) {
        slotEnd = moment(slotStart).add(simulationDuration, 'days')
      } else {
        slotEnd = moment(new Date(endDate.replace('Z', ''))).startOf('day')
      }

      // Check if the selected date falls within the slot's range
      if (selectedMoment.isSameOrAfter(slotStart) && selectedMoment.isSameOrBefore(slotEnd)) {
        const task = taskList.find(({scheduleList}) => scheduleList && scheduleList.some(({id}) => id === scheduleId))
        if (task) {
          taskIdsForDay.add(task.id)
        }
      }
    })

    return taskList.filter((task) => taskIdsForDay.has(task.id))
  }

  const tasksForSelectedDay = getTasksForSelectedDay()

  const events = (() => {
    // First, map schedule time slots to events with task info
    const rawEvents = scheduleTimeSlotList.map(({scheduleId, startDate: slotStartDate, endDate}) => {
      const task = taskList.find(({scheduleList}) => scheduleList && scheduleList.some(({id}) => id === scheduleId))
      const taskName = task ? task[getTaskContentName(task.type)]?.name : 'Unknown Task'
      const start = new Date(slotStartDate.replace('Z', ''))
      let end
      if (!endDate) {
        end = moment(start).add(simulationDuration, 'days').toDate()
      } else {
        end = new Date(endDate.replace('Z', ''))
      }
      return {
        taskName,
        start,
        end,
        style: taskColorTranslator(task?.color ?? color.task.green),
        // Create a key for grouping: task name + day
        groupKey: `${taskName}-${moment(start).format('YYYY-MM-DD')}`,
      }
    })

    // Group events by task name and day
    const groupedEvents = _.groupBy(rawEvents, 'groupKey')
    // Create final events with count
    return Object.values(groupedEvents).map((group) => {
      const first = group[0]
      const count = group.length
      const title = count > 1 ? `${first.taskName} [x${count}]` : first.taskName
      return {
        title,
        start: first.start,
        end: first.end,
        allDay: false,
        style: first.style,
      }
    })
  })()

  const nextMonth = () => {
    const dateA = moment(bigCalendarDate).add(1, 'month')
    const dateB = moment(startDate).add(simulationDuration, 'days')
    if (dateA.isSameOrBefore(dateB, 'month')) setBigCalendarDate(dateA.toDate())
  }

  const prevMonth = () => {
    const dateA = moment(bigCalendarDate).subtract(1, 'month')
    const dateB = moment(startDate)
    if (dateA.isSameOrAfter(dateB, 'month')) setBigCalendarDate(dateA.toDate())
  }

  const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    if (value !== '') {
      setDuration(Math.min(365, parseInt(value)))
    } else {
      setDuration('')
    }
  }

  useDebounce(
    () => {
      setSimulationDuration(duration)
    },
    500,
    [duration],
  )

  const CalendarDay = (props: {date: Date}) => {
    const momentDate = moment(props.date).startOf('day')
    const momentStartDate = moment(startDate).startOf('day')
    const d = typeof simulationDuration === 'number' ? simulationDuration : 30
    return (
      <div
        css={{
          width: 'calc(100%  - 16px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          margin: '8px',
        }}>
        <div>
          {momentDate.isSameOrAfter(momentStartDate, 'day') && momentDate.diff(momentStartDate, 'days') < d && (
            <span css={{fontSize: fontSize.h7, fontWeight: fontWeight.bold}}>
              Day {momentDate.diff(momentStartDate, 'days') + 1}
            </span>
          )}
        </div>
        <span
          css={{
            fontSize: fontSize.h7,
            fontWeight: fontWeight.bold,
            color: color.textIcon.description,
          }}>
          {momentDate.format('MMM D')}
        </span>
      </div>
    )
  }

  return (
    <div
      css={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1000,
      }}>
      <div
        css={{
          width: '100%',
          height: '48px',
          padding: '0 30px',
          display: 'flex',
          alignItems: 'center',
          backgroundColor: color.surface.white,
          boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.04)',
          zIndex: 99,
        }}>
        <ButtonCancel onClick={() => setShowFullCalendar(false)} />
      </div>
      <div
        css={{
          display: 'flex',
          width: '100%',
          height: 'calc(100vh - 48px)',
        }}>
        <div
          css={{
            padding: '40px 30px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            flex: 1,
            backgroundColor: color.surface.default,
          }}>
          <p
            css={{
              fontSize: fontSize.h3,
              fontWeight: fontWeight.thick,
            }}>
            Simulated Task Schedule for Participants (based on join date)
          </p>
          <div
            css={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '24px',
            }}>
            {/* date picker */}
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}>
              <div css={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                <span css={{fontSize: fontSize.h7, color: color.textIcon.secondary}}>Participant joined date</span>
                <Tooltip content={''} />
              </div>
              <DatePickerContainer>
                <DatePicker
                  {...{
                    selected: startDate,
                    onChange: (date) => {
                      setStartDate(date ?? new Date())
                    },
                    minDate: new Date(),
                    maxDate: moment().add(1, 'year').toDate(),
                  }}
                />
              </DatePickerContainer>
            </div>
            {/* duration */}
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}>
              <div css={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                <label
                  htmlFor='full-calendar-duration-input'
                  css={{fontSize: fontSize.h7, color: color.textIcon.secondary, cursor: 'text'}}>
                  Duration
                </label>
                <Tooltip content={''} />
              </div>
              <div
                css={{
                  width: '160px',
                  height: '32px',
                  border: `1px solid ${color.border.default}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '6px 8px 6px 12px',
                  borderRadius: '5px',
                  backgroundColor: 'white',
                }}>
                <input
                  id='full-calendar-duration-input'
                  type='text'
                  aria-label='Duration in days'
                  value={duration}
                  onChange={handleDurationChange}
                  onKeyDown={(e) => {
                    if (
                      e.key === 'Backspace' ||
                      e.key === 'Delete' ||
                      e.key === 'Tab' ||
                      e.key === 'ArrowLeft' ||
                      e.key === 'ArrowRight'
                    ) {
                      return
                    }

                    if (!/^[0-9]$/.test(e.key)) {
                      e.preventDefault()
                    }
                  }}
                  css={{
                    width: '80px',
                    height: '100%',
                    border: 'none',
                    backgroundColor: 'transparent',
                    fontSize: fontSize.h7,
                    color: color.textIcon.secondary,
                    outline: 'none',
                  }}
                />
                <span
                  css={{
                    fontSize: fontSize.h7,
                    color: color.textIcon.secondary,
                  }}>
                  Days
                </span>
              </div>
            </div>
          </div>
          <div
            css={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <div
              css={{
                width: '100%',
                padding: '16px 32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '40px',
                border: `1px solid ${color.border.default}`,
                borderBottom: 'none',
                backgroundColor: color.surface.white,
                borderTopLeftRadius: '5px',
                borderTopRightRadius: '5px',
              }}>
              <button
                onClick={prevMonth}
                css={{
                  border: 'none',
                  backgroundColor: 'white',
                  transform: 'rotate(90deg)',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  borderRadius: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ':hover': {
                    backgroundColor: color.surface.hover.hoverWhiteBg,
                  },
                }}>
                <ChevronDownIcon width={12} height={12} />
              </button>
              <span css={{fontSize: fontSize.h6, fontWeight: fontWeight.thick}}>
                {format(bigCalendarDate, 'MMM, yyyy')}
              </span>
              <button
                onClick={nextMonth}
                css={{
                  border: 'none',
                  backgroundColor: 'white',
                  transform: 'rotate(270deg)',
                  cursor: 'pointer',
                  width: '32px',
                  height: '32px',
                  borderRadius: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ':hover': {
                    backgroundColor: color.surface.hover.hoverWhiteBg,
                  },
                }}>
                <ChevronDownIcon width={12} height={12} />
              </button>
            </div>
            <BigCalendar
              {...{
                style: {border: 'none'},
                date: bigCalendarDate,
                localizer,
                events,
                hideHeader: true,
                views: ['month'],
                toolbar: false,
                selectable: true,
                onSelectSlot: handleDayClick,
                components: {
                  month: {dateHeader: CalendarDay},
                },
                dayPropGetter: (date: Date) => {
                  const isSelected =
                    selectedDate && moment(date).startOf('day').isSame(moment(selectedDate).startOf('day'))
                  const momentDate = moment(date).startOf('day')
                  const momentStartDate = moment(startDate).startOf('day')
                  const d = typeof simulationDuration === 'number' ? simulationDuration : 30
                  const isInRange =
                    momentDate.isSameOrAfter(momentStartDate, 'day') && momentDate.diff(momentStartDate, 'days') < d
                  const backgroundColor = isInRange ? color.surface.white : color.surface.default

                  return {
                    style: {
                      backgroundColor,
                      ...(isSelected
                        ? {
                            outline: `2px solid ${color.textIcon.title}`,
                            outlineOffset: '-2px',
                            borderRadius: '5px',
                          }
                        : {}),
                    },
                  }
                },
                eventPropGetter: (event: Event & {style: CSSProperties}) => {
                  return {
                    style: {
                      borderRadius: 0,
                      borderLeft: event.style.borderLeft,
                      backgroundColor: event.style.backgroundColor,
                      margin: '1px 8px',
                      height: '21px',
                      width: 'calc(100% - 16px)',
                      pointerEvents: 'none' as const,
                      position: 'relative',
                      zIndex: 1,
                    },
                  }
                },
              }}
            />
          </div>
        </div>
        <div
          css={{
            width: '428px',
            height: '100%',
            backgroundColor: color.surface.nested,
            padding: '32px 24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}>
          {selectedDate ? (
            <>
              <div css={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                <button
                  onClick={handleBackToAllTasks}
                  css={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    transform: 'rotate(90deg)',
                    cursor: 'pointer',
                    width: '32px',
                    height: '32px',
                    borderRadius: '5px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    ':hover': {
                      backgroundColor: color.surface.hover.hoverWhiteBg,
                    },
                  }}>
                  <ChevronDownIcon width={16} height={16} />
                </button>
                <div css={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                  <span css={{fontSize: fontSize.h4, fontWeight: fontWeight.bold}}>
                    {moment(selectedDate).format('MMM D, YYYY')}
                  </span>
                  <span css={{fontSize: fontSize.h7, color: color.textIcon.description}}>
                    {tasksForSelectedDay.length > 0
                      ? `${tasksForSelectedDay.length} task${tasksForSelectedDay.length > 1 ? 's' : ''} scheduled`
                      : 'No tasks scheduled'}
                  </span>
                </div>
              </div>
              <div
                css={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  overflowY: 'auto',
                }}>
                {tasksForSelectedDay.length > 0 ? (
                  tasksForSelectedDay.map((task, index) => <TaskCard key={task.id} {...{item: task, index}} />)
                ) : (
                  <p
                    css={{
                      color: color.textIcon.description,
                      textAlign: 'center',
                      padding: '24px 0',
                    }}>
                    No tasks are scheduled for this day.
                  </p>
                )}
              </div>
            </>
          ) : (
            <>
              <span css={{fontSize: fontSize.h4, fontWeight: fontWeight.bold}}>All Tasks</span>
              <div
                css={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  overflowY: 'auto',
                }}>
                {scheduleTaskList.map((task, index) => (
                  <TaskCard {...{item: task, index}} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
